"use client";

import dynamic from "next/dynamic";
import { useEffect, useState } from "react";

import { DssCover } from "@/components/dss-cover";
import { FieldNoteFab } from "@/components/field-note-fab";
import { DOG_SALON_SNAPS_PRODUCT } from "@/domain/product";

type Mode = "flow" | "reservations" | "newPet" | "archive" | "desk" | "pricing" | "ops";
type DeskTab = "request" | "faq" | "manual" | "log";
type UiScale = "sm" | "md" | "lg";

const SCALE_KEY = "dog-salon-snaps-ui-scale";
const COVER_KEY = "dss-cover-entered";

const MAIN_TABS: { mode: Exclude<Mode, "desk" | "pricing" | "ops">; label: string; full: string }[] = [
  { mode: "flow", label: "今日", full: "今日" },
  { mode: "reservations", label: "スケジュール", full: "スケジュール" },
  { mode: "archive", label: "アルバム", full: "アルバム" },
  { mode: "newPet", label: "新規", full: "新規登録" },
];

function PanelLoading() {
  return <p className="muted panel-loading">読み込み中…</p>;
}

const ChronologicalStudio = dynamic(
  () =>
    import("@/components/chronological-studio").then((m) => m.ChronologicalStudio),
  { loading: PanelLoading, ssr: false },
);
const ReservationBoard = dynamic(
  () => import("@/components/reservation-board").then((m) => m.ReservationBoard),
  { loading: PanelLoading, ssr: false },
);
const NewPetRegister = dynamic(
  () => import("@/components/new-pet-register").then((m) => m.NewPetRegister),
  { loading: PanelLoading, ssr: false },
);
const VisitArchive = dynamic(
  () => import("@/components/visit-archive").then((m) => m.VisitArchive),
  { loading: PanelLoading, ssr: false },
);
const OwnerDesk = dynamic(
  () => import("@/components/owner-desk").then((m) => m.OwnerDesk),
  { loading: PanelLoading, ssr: false },
);
const PricingDesk = dynamic(
  () => import("@/components/pricing-desk").then((m) => m.PricingDesk),
  { loading: PanelLoading, ssr: false },
);
const SalonOpsDesk = dynamic(
  () => import("@/components/salon-ops-desk").then((m) => m.SalonOpsDesk),
  { loading: PanelLoading, ssr: false },
);

export function SalonSnapApp() {
  const [showCover, setShowCover] = useState(true);
  const [mode, setMode] = useState<Mode>("flow");
  const [archiveKey, setArchiveKey] = useState(0);
  const [reservationKey, setReservationKey] = useState(0);
  const [flowKey, setFlowKey] = useState(0);
  const [newPetKey, setNewPetKey] = useState(0);
  const [deskKey, setDeskKey] = useState(0);
  const [deskTab, setDeskTab] = useState<DeskTab>("request");
  const [scale, setScale] = useState<UiScale>(() => {
    if (typeof window === "undefined") return "md";
    const saved = window.localStorage.getItem(SCALE_KEY);
    return saved === "sm" || saved === "md" || saved === "lg" ? saved : "md";
  });

  useEffect(() => {
    document.documentElement.dataset.uiScale = scale;
    window.localStorage.setItem(SCALE_KEY, scale);
  }, [scale]);

  useEffect(() => {
    void requestPersistentStorage();
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (window.sessionStorage.getItem(COVER_KEY) === "1") {
      setShowCover(false);
    }
  }, []);

  function enterFromCover() {
    window.sessionStorage.setItem(COVER_KEY, "1");
    setShowCover(false);
  }

  if (showCover) {
    return <DssCover onEnter={enterFromCover} />;
  }

  return (
    <div className="app-shell">
      <header className="studio-header">
        <div className="header-top">
          <button
            type="button"
            className="brand-row brand-row-btn"
            onClick={() => {
              window.sessionStorage.removeItem(COVER_KEY);
              setShowCover(true);
            }}
            title="表紙に戻る"
            aria-label="表紙に戻る"
          >
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img
              className="brand-icon"
              src={DOG_SALON_SNAPS_PRODUCT.appIconPath}
              alt=""
              width={48}
              height={48}
              decoding="async"
              fetchPriority="low"
            />
            <div className="brand-text">
              <p className="brand">{DOG_SALON_SNAPS_PRODUCT.shortEn}</p>
              <p className="brand-full">{DOG_SALON_SNAPS_PRODUCT.productNameEn}</p>
            </div>
          </button>
        </div>
      </header>

      <nav className="tabs tabs-4" aria-label="メイン">
        {MAIN_TABS.map((tab) => (
          <button
            key={tab.mode}
            type="button"
            className={mode === tab.mode ? "tab active" : "tab"}
            title={tab.full}
            aria-label={tab.full}
            onClick={() => {
              if (tab.mode === "flow") setFlowKey((k) => k + 1);
              if (tab.mode === "reservations") setReservationKey((k) => k + 1);
              if (tab.mode === "newPet") setNewPetKey((k) => k + 1);
              if (tab.mode === "archive") setArchiveKey((k) => k + 1);
              setMode(tab.mode);
            }}
          >
            <span className="tab-label">{tab.label}</span>
          </button>
        ))}
      </nav>

      {mode === "flow" ? (
        <ChronologicalStudio
          key={flowKey}
          onOpenReservations={() => {
            setReservationKey((k) => k + 1);
            setMode("reservations");
          }}
          onOpenNewPet={() => {
            setNewPetKey((k) => k + 1);
            setMode("newPet");
          }}
          onSaved={() => {
            setArchiveKey((k) => k + 1);
            setMode("archive");
          }}
        />
      ) : null}
      {mode === "reservations" ? (
        <ReservationBoard
          refreshKey={reservationKey}
          onOpenNewPet={() => {
            setNewPetKey((k) => k + 1);
            setMode("newPet");
          }}
        />
      ) : null}
      {mode === "newPet" ? (
        <NewPetRegister
          key={newPetKey}
          onOpenReservations={() => {
            setReservationKey((k) => k + 1);
            setMode("reservations");
          }}
        />
      ) : null}
      {mode === "archive" ? <VisitArchive refreshKey={archiveKey} /> : null}
      {mode === "pricing" ? <PricingDesk /> : null}
      {mode === "ops" ? <SalonOpsDesk /> : null}
      {mode === "desk" ? (
        <OwnerDesk key={`${deskKey}-${deskTab}`} refreshKey={deskKey} initialTab={deskTab} />
      ) : null}

      <FieldNoteFab
        onOpenDesk={() => {
          setDeskTab("log");
          setDeskKey((k) => k + 1);
          setMode("desk");
        }}
      />

      <footer className="app-bottom-bar" aria-label="表示と窓口">
        <div className="appear-row" aria-label="文字サイズ">
          {(["sm", "md", "lg"] as const).map((s) => (
            <button
              key={s}
              type="button"
              className={scale === s ? "appear-btn active" : "appear-btn"}
              onClick={() => setScale(s)}
              title={s === "sm" ? "文字を小さく" : s === "md" ? "標準" : "文字を大きく"}
            >
              {s === "sm" ? "小" : s === "md" ? "中" : "大"}
            </button>
          ))}
        </div>
        <div className="bottom-actions">
          <button
            type="button"
            className={mode === "pricing" ? "desk-btn active" : "desk-btn"}
            onClick={() => setMode("pricing")}
          >
            料金
          </button>
          <button
            type="button"
            className={mode === "ops" ? "desk-btn active" : "desk-btn"}
            onClick={() => setMode("ops")}
          >
            運営
          </button>
          <button
            type="button"
            className={mode === "desk" ? "desk-btn active" : "desk-btn"}
            onClick={() => {
              setDeskTab("request");
              setDeskKey((k) => k + 1);
              setMode("desk");
            }}
          >
            窓口
          </button>
        </div>
      </footer>
    </div>
  );
}
